import BASE from "./config";

const GBP_CODES = new Set(["GBP", "GPB", "£", "POUND", "POUNDS", "STERLING"]);
const RATE_CACHE_KEY = "sunboundGbpEurMidRate";
const RATE_TTL_MS = 12 * 60 * 60 * 1000;

const isGbp = (currency) => {
    if (currency == null || currency === "") return false;
    if (typeof currency === "object") {
        return isGbp(currency.code) || isGbp(currency.symbol);
    }
    return GBP_CODES.has(String(currency).trim().toUpperCase());
};

const numericAmount = (value) => {
    if (value == null || value === "") return null;
    const raw = parseFloat(String(value).replace(/[^\d.-]/g, ""));
    return Number.isFinite(raw) ? raw : null;
};

const formatEuro = (amount, multiplier) => String(Math.round(amount * multiplier));

let rateRequest = null;

const readCachedRate = () => {
    try {
        const cached = JSON.parse(sessionStorage.getItem(RATE_CACHE_KEY) || "null");
        if (!cached || Date.now() - cached.savedAt > RATE_TTL_MS) return null;
        return Number(cached.rate) > 0 ? Number(cached.rate) : null;
    } catch {
        return null;
    }
};

const writeCachedRate = (rate) => {
    try {
        sessionStorage.setItem(RATE_CACHE_KEY, JSON.stringify({ rate, savedAt: Date.now() }));
    } catch {
        /* private mode */
    }
};

let chargeRequest = null;

const getBankChargePercent = () => {
    const fallback = Number(BASE.GBP_EUR_BANK_CHARGE_PERCENT) || 0;
    if (!chargeRequest) {
        chargeRequest = fetch(`${BASE.BASE_API_URL}/api/exchange-rate`)
            .then((response) => response.json())
            .then((data) => {
                const value = Number(data?.bank_charge_percent);
                return Number.isFinite(value) ? value : fallback;
            })
            .catch(() => fallback);
    }
    return chargeRequest;
};

const getMidMarketRate = () => {
    const cached = readCachedRate();
    if (cached) return Promise.resolve(cached);

    if (!rateRequest) {
        rateRequest = fetch("https://api.frankfurter.app/latest?from=GBP&to=EUR")
            .then((response) => response.json())
            .then((data) => {
                const rate = Number(data?.rates?.EUR);
                if (!Number.isFinite(rate) || rate <= 0) return null;
                writeCachedRate(rate);
                return rate;
            })
            .catch(() => null)
            .finally(() => {
                rateRequest = null;
            });
    }

    return rateRequest;
};

export const getGbpToEurMultiplier = async () => {
    const [charge, rate] = await Promise.all([getBankChargePercent(), getMidMarketRate()]);
    if (!rate) return null;
    return rate * (1 + charge / 100);
};

const convertPriceRow = (row, multiplier) => {
    if (!row || !isGbp(row.currency)) return row;
    const amount = numericAmount(row.price ?? row.amount);
    const next = { ...row, currency: "EUR" };
    if (amount == null) return next;
    const euros = formatEuro(amount, multiplier);
    next.price = euros;
    if (row.amount != null) next.amount = euros;
    return next;
};

const convertProperty = (property, multiplier) => {
    if (!property || typeof property !== "object" || Array.isArray(property)) return property;

    const prices = Array.isArray(property.property_prices) ? property.property_prices : null;
    const baseIsGbp = prices?.some((row) => isGbp(row?.currency)) ?? false;
    const next = { ...property };

    if (prices) {
        next.property_prices = prices.map((row) => convertPriceRow(row, multiplier));
    }

    if (baseIsGbp) {
        const airCon = numericAmount(property.air_con_price);
        const pool = numericAmount(property.pool_heating_price);
        if (airCon != null) next.air_con_price = formatEuro(airCon, multiplier);
        if (pool != null) next.pool_heating_price = formatEuro(pool, multiplier);
    }

    const extras = property.members?.extra_charges;
    if (Array.isArray(extras)) {
        next.members = {
            ...property.members,
            extra_charges: extras.map((charge) => {
                if (!isGbp(charge?.currency)) return charge;
                const amount = numericAmount(charge.price);
                return {
                    ...charge,
                    price: amount == null ? charge.price : formatEuro(amount, multiplier),
                    currency: {
                        ...(typeof charge.currency === "object" ? charge.currency : {}),
                        code: "EUR",
                        symbol: "€",
                    },
                };
            }),
        };
    }

    if (Array.isArray(property.properties)) {
        next.properties = property.properties.map((item) => convertProperty(item, multiplier));
    }

    return next;
};

export const containsGbpPrices = (data) => {
    if (!data) return false;
    if (Array.isArray(data)) return data.some(containsGbpPrices);
    if (typeof data !== "object") return false;
    if (Array.isArray(data.property_prices) && data.property_prices.some((row) => isGbp(row?.currency))) {
        return true;
    }
    if (Array.isArray(data.members?.extra_charges) && data.members.extra_charges.some((row) => isGbp(row?.currency))) {
        return true;
    }
    if (Array.isArray(data.properties) && data.properties.some(containsGbpPrices)) return true;
    return false;
};

export const convertDisplayedPrices = async (data) => {
    if (!containsGbpPrices(data)) return data;
    const multiplier = await getGbpToEurMultiplier();
    if (!multiplier) return data;
    if (Array.isArray(data)) return data.map((item) => convertProperty(item, multiplier));
    return convertProperty(data, multiplier);
};
